import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tags, Loader2, Undo2 } from "lucide-react";
import { BrandIcon } from "@/components/BrandIcon";

export function SourceBackfill({ onChanged }) {
  const [d, setD] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setD(null);
    api.get("/enquiry-outcomes/source-backfill").then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (d?.error) return null;
  const act = async (fn, ok) => {
    setBusy(true);
    try { const r = await fn(); toast.success(ok(r.data)); setConfirm(false); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); load(); }
    finally { setBusy(false); }
  };
  const apply = () => act(() => api.post("/enquiry-outcomes/source-backfill", { expected_count: d.count }), (x) => `Source filled on ${x.updated} enquiries`);
  const undo = () => act(() => api.post("/enquiry-outcomes/source-backfill/undo", { batch_id: d.last_batch.batch_id }), (x) => `${x.restored} enquiries reset to no source`);
  const btn = "inline-flex items-center gap-1.5 rounded-[8px] h-10 px-4 text-sm font-medium disabled:opacity-50";
  return (
    <section className="bg-white border border-slate-200 rounded-[8px] p-5 space-y-3" data-testid="source-backfill">
      <div>
        <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Tags className="w-4 h-4 text-slate-500" /> Fill in missing sources</h2>
        <p className="text-xs text-slate-500 mt-0.5">Older enquiries with no source, whose title names one (e.g. "Facebook Swim Spa Enquiry"). Only blank sources are filled; existing ones are never changed.</p>
      </div>
      {!d ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="sb-by-source">
            {Object.entries(d.by_source).map(([n, c]) => (
              <span key={n} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-0.5 text-xs text-slate-700"><BrandIcon name={n} size={14} />{n} <b className="tabular-nums">{c}</b></span>
            ))}
            {!d.count && <span className="text-sm text-slate-400">No older enquiries can be matched from their titles.</span>}
          </div>
          {d.sample.length > 0 && (
            <ul className="text-xs divide-y divide-slate-100 border border-slate-100 rounded-[8px] max-h-44 overflow-y-auto" data-testid="sb-sample">
              {d.sample.map((s) => <li key={s.legacy_id} className="px-3 py-1.5 flex justify-between gap-3"><span className="truncate text-slate-700">{s.legacy_id} · {s.title}</span><span className="shrink-0 text-slate-500">→ {s.source}</span></li>)}
              {d.count > d.sample.length && <li className="px-3 py-1.5 text-slate-400">…and {d.count - d.sample.length} more</li>}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {!confirm ? (
              <button type="button" disabled={!d.count || busy} onClick={() => setConfirm(true)} className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white`} data-testid="sb-start">Fill source on {d.count}</button>
            ) : (
              <>
                <span className="text-sm text-amber-700">Fill the source on {d.count} enquiries?</span>
                <button type="button" disabled={busy} onClick={apply} className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white`} data-testid="sb-confirm">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Yes, fill in</button>
                <button type="button" onClick={() => setConfirm(false)} className={`${btn} border border-slate-200 text-slate-600`} data-testid="sb-cancel">Cancel</button>
              </>
            )}
            {d.last_batch && <button type="button" disabled={busy} onClick={undo} className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`} data-testid="sb-undo"><Undo2 className="w-4 h-4" /> Undo last fill</button>}
          </div>
        </>
      )}
    </section>
  );
}
